import { defineStore } from "pinia";
import { API_Labs_orders, LabOrder } from "@/services/api/Labs/orders";
import { API_Labs_research, LabsResearch } from "@/services/api/Labs/research";
import { getIsoDate, getRuDate } from "@/helpers/dateUtils";
import { API_Labs_research_result, LabsResearchResult } from "@/services/api/Labs/research_result";
import { computed } from "vue";

interface LabStructureItem {
  label?: string; hide?: boolean; type?: 'Date' | "Text"; value?: string;
}

const labsStructure: Record<string, LabStructureItem> = {
  srvtext: { label: "Название услуги или общего исследования" },
  test_name: { label: "Название теста (анализа)" },
  order_num: { label: "Номер заказа (направления) на исследование" },
  ids: { hide: true },
  specimen_text: { label: "Тип биоматериала (образца)" },
  regdate: {type: "Date", label: "Дата и время регистрации заказа в системе" },
  title_id: { hide: true  },
  title: { label: "Название заголовка (группы)" },
  res_display_order: { hide: true },
  value: { label: "Результат анализа (значение)" },
  value_note: { label: "Примечание к результату" },
  unit: { label: "Единица измерения (для количественных тестов)" },
  norm: { label: "Референсные значения (норма)" },
  pat_level: { label: "Уровень патологии (интерпретация: выше/ниже нормы)" },
  norm_min: { label: "Нижняя граница нормы" },
  norm_max: { label: "Верхняя граница нормы" },
  norm_note: { label: "Примечание к референсным значениям" },
  last_res: { hide: true },
  data_last_res: { hide: true },
  abio_bactery_text: { label: "Название бактерии (для микробиологических исследований)" },
  abio_bactery_sortcode: { hide: true },
  abio_koe: { label: "Колониеобразующие единицы (КОЕ)" },
  res_bactery_text: { label: "Результат по бактерии" },
  res_antibiotic_text: { label: "Название антибиотика" },
  res_sortcode: { hide: true },
  res_sir: { label: "Интерпретация чувствительности к антибиотику" },
  res_mic: { label: "Минимальная ингибирующая концентрация (MIC)" },
  res_dia: { label: "Диаметр зоны ингибиции" },
  abio_conclusion: { label: "Заключение по антибиотикограмме" },
  abio_note: { label: "Примечание к антибиотикограмме" },
  fin: { label: "Источник финансирования (например, договор, ОМС, ДМС)" },
  collectdate: { type: "Date",  label: "Дата и время взятия биоматериала" },
  finishdate: { type: "Date", label: "Дата и время окончания анализа" },
  exec_man: { label: "ФИО сотрудника, выполнившего анализ" },
  valid_man: { label: "ФИО сотрудника, утвердившего результат" },
};
const labStructureArr = Object.entries(labsStructure);

export class LabInfoData {
  labResearchInfo: LabsResearch;
  labResearchResultIsLoading = false;
  labResearchResult: LabsResearchResult[] = [];
  researchId: number | null = null;


  constructor(labResearchData: LabsResearch, researchId: number | null = null) {
    this.labResearchInfo = labResearchData;
    this.researchId = researchId;
  }

  loadResult() {

    this.labResearchResultIsLoading = true;
    API_Labs_research_result({
      rchild: String(this.labResearchInfo.research_id),
      type: 'ext',
    }).then((labResearchResultList) => {
      this.labResearchResult.length = 0;
      labResearchResultList.forEach(labResearchResultItem => this.labResearchResult.push(labResearchResultItem))
    }).finally(() => {
      this.labResearchResultIsLoading = false;
    });
  }

  /**
   * Данные анализа в обработаном виде для всех анализов карточки
   */
  getResearchInfo(): {title: string; resultArr: LabStructureItem[]}[] {

      const labDataCollectorList: {title: string; resultArr: LabStructureItem[]}[] = [];

      if (this.labResearchInfo) {
        const labResultItem = this.labResearchInfo;
        const labResultGroupItem = {
          resultArr: [] as LabStructureItem[],
          title: labResultItem.specimen ? `${ labResultItem.specimen } - ${labResultItem.research_id}` : "-"
        };

        labStructureArr.forEach(([key, labsStructureItem]) => {
          let val = labResultItem[key as keyof LabsResearch];
          if (val === null || val === undefined) return;
          val = String(val);

          if (!labsStructureItem) {
            labResultGroupItem.resultArr.push({
              label: key,
              value: val ?? '-'
            });
            return;
          }


          if (labsStructureItem.hide) return;
          let value = val;
          switch (labsStructureItem.type) {
            case "Date":
              value = getRuDate(new Date(val));
              break;
          }

          labResultGroupItem.resultArr.push({
            label: labsStructureItem.label ?? key,
            value: value ?? '-',
            type: labsStructureItem.type ?? "Text"
          });
        });

        labDataCollectorList.push(labResultGroupItem);

      }
      return labDataCollectorList;

  }

}

export class LabData {
  short: LabOrder;
  full: LabInfoData[] = [];
  isLoading = false;
  fullIsLoading = false;

  constructor(short: LabOrder) {
    this.short = short;
  }

  loadFull() {
    if (this.full !== null && this.full.length > 0)
      return Promise.resolve(this.full);

    if (!this.short || this.short.research_id === null)
      throw new Error("short dict is null");
    this.fullIsLoading = true;
    return new Promise((resolve, reject) => {
      API_Labs_research({
        researchid: `${this.short.research_id}`,
      })
        .then((labResearchInfoList) => {
          this.full.length = 0;
          labResearchInfoList.forEach((labResearchItem) => this.full.push(new LabInfoData(labResearchItem)));
          resolve(this.full);
        })
        .catch((err) => reject(err))
        .finally(() => (this.fullIsLoading = false));
    });
  }
}

interface ILabStore {
  isLoading: boolean;
  rawData: LabData[];
}

export const useUserLabsStore = defineStore({
  id: "userLabs",
  state: (): ILabStore => {
    return {
      isLoading: false,
      rawData: [],
    };
  },
  getters: {
    data: (state) => state.rawData,
    dataIsLoading: (state) => state.isLoading,
  },
  actions: {
    loadAll({ start, end }: { start?: number; end?: number } = {}) {
      this.isLoading = true;

      return new Promise<LabData[]>((resolve, reject) => {
        const nowDate = new Date();
        const toDate = new Date();
        const fromDate = new Date();

        toDate.setFullYear(nowDate.getFullYear() + 1);
        fromDate.setFullYear(nowDate.getFullYear() - 1);

        API_Labs_orders({
          beginDate: getIsoDate(fromDate),
          endDate: getIsoDate(toDate),
          start: start ?? 1,
          end: end ?? 9999,
          orderby: "ASC",
        })
          .then((response) => {
            this.rawData.length = 0;
            response.forEach((short) => {
              const labDataItem = new LabData(short)

              labDataItem.loadFull()

              // this.isLoading = labDataItem.fullIsLoading;

              this.rawData.push(labDataItem)
            });
            resolve(this.rawData);
          })
          .catch((err) => {
            reject(err);
          })
          .finally(() => (this.isLoading = false));
      });
    },
  },
});
